import { useRemoteClientGet, useRemoteClientPost, useRemoteClientPut } from '@/configs/http-client'
import { Endpoints } from '@/remote/endpoints'
import { useLocalStorage } from '@/remote/local-storage'
import { zodResolver } from '@hookform/resolvers/zod'
import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { NumericFormat } from 'react-number-format'
import { useSearchParams } from 'react-router-dom'
import Select from 'react-select'
import { useHookFormMask } from 'use-mask-input'
import { ComboSelectWithLabel } from '../components/combo-select-with-label'
import { CustomSelect } from '../components/custom-select'
import { SwitchDemo } from '../components/switch-label'
import { Button, Input } from '../components/ui/components'
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '../components/ui/form'
import { SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select'
import { Textarea } from '../components/ui/textarea'
import { useToast } from '../hooks'
import { loadAllContracts } from '../pages/dashboard-owner-gym/contracts/actions/load-all-contracts'
import { applyMaskBrlCurrency, applyMaskBrlCurrencyFloat, formatCurrencyToNumber } from '../utils/formatCurrency'
import { formatDateString, validBoolean } from '../utils/formatDate'
import { convertDateDisplayFormat } from '../utils/formatDateDisplay'
import { formatParams } from '../utils/pagination'
import { CostCenterFormSchema } from '../validators/cost-center-validator'
import { PlanFormData, planoSchema } from '../validators/plan-validator'
import { UnitsFormData } from '../validators/units-validator'

interface PlanFormProps {
  data?: any
  closeDialog?: () => void
  updateList?: () => void
}

export default function PlanForm({ data, closeDialog, updateList }: PlanFormProps) {
  const { httpClientPost } = useRemoteClientPost()
  const { httpClientPut } = useRemoteClientPut()
  const { remoteClientGet } = useRemoteClientGet()
  const { useFetchLocalStore, getUnitId } = useLocalStorage()
  const { showToast } = useToast()
  const [quantityLimit, setQuantityLimit] = useState<boolean>(false)
  const [params] = useSearchParams()
  const [dataUnits, setDataUnits] = useState<any>([])
  const [selectedUnits, setSelectedUnits] = useState<any>([])
  const [dataCostCenter, setDataCostCenter] = useState<any>([])
  const isEditMode = Boolean(data?.id)

  const { data: contracts, reloadTemplates: reloadContracts } = loadAllContracts()

  const form = useForm<PlanFormData>({
    resolver: zodResolver(planoSchema),
    defaultValues: {
      id: data?.id || 0,
      nome: data?.nome || '',
      descricao: data?.descricao || '',
      vendaOnline: validBoolean(data?.vendaOnline),
      comissaoPercentual: validBoolean(data?.comissaoPercentual),
      valor: applyMaskBrlCurrencyFloat(data?.valor || ''),
      maximoParcelas: data?.maximoParcelas.toString() || '1',
      valorComissao: applyMaskBrlCurrencyFloat(data?.valorComissao || ''),
      valorCusto: applyMaskBrlCurrencyFloat(data?.valorCusto || ''),
      unidade: {
        id: data?.unidade?.id || getUnitId(),
        nomeFantasia: ''
      },
      centroCusto: {
        id: data?.centroCusto?.id || ''
      },
      contrato: {
        id: (data?.contrato?.id.toString() || 0) as any
      },
      academiaId: data?.academiaId || useFetchLocalStore().idAcademia,
      dataInicio: convertDateDisplayFormat(data?.dataInicio.split('T')[0] || ''),
      dataVencimento: convertDateDisplayFormat(data?.dataVencimento?.split('T')[0] || ''),
      quantidade: data?.quantidade || 1,
      valorDesconto: applyMaskBrlCurrencyFloat(data?.valorDesconto || ''),
      observacao: data?.observacao || ''
    }
  })

  const feactDataCostCenter = async () => {
    try {
      const response = await remoteClientGet(
        Endpoints.costCenter,
        formatParams({
          page: 0,
          size: 1000,
          sortParam: 'id',
          order: 'desc',
          unidadeId: getUnitId() || params.get('unit')
        })
      )
      const data = response?.data?.data?.content as CostCenterFormSchema[]
      const dataForm = data.map((elem) => {
        return { label: elem.descricao, value: elem.id }
      })
      setDataCostCenter(dataForm)
    } catch (error) {
      console.error('Erro ao buscar dados:', error)
      throw error
    }
  }

  const fetchDataUnits = async () => {
    try {
      const response = await remoteClientGet(
        Endpoints.units + '/usuario',
        formatParams({
          page: 0,
          size: 1000,
          sortParam: 'id',
          order: 'desc'
        })
      )
      const units = response?.data?.data.map((unit: any) => ({
        ...unit,
        label: unit?.nomeFantasia,
        value: unit?.id
      })) as UnitsFormData[]
      setDataUnits(units)
      if (data) {
        console.log('data', data)
        console.log('units', units)

        setSelectedUnits(units.filter((unit) => data.unidades.some((d: any) => d.id === unit.id)))

        console.log('unit', units)
      }
    } catch (error) {
      console.error('Erro ao buscar dados:', error)
      throw error
    }
  }

  useEffect(() => {
    fetchDataUnits()
    feactDataCostCenter()
  }, [])

  useEffect(() => {
    const calcularValores = () => {
      const parseCurrency = (value: string) => {
        return parseFloat(value.replace(/[^0-9,]/g, '').replace(',', '.')) || 0
      }

      let valorDesconto = parseCurrency(form.getValues('valorDesconto'))
      let valorComissao = parseCurrency(form.getValues('valorComissao'))
      const valor = parseCurrency(form.getValues('valor'))

      if (valorComissao > valor * 0.3) {
        console.log('Ajustando comissão...')
        valorComissao = valor * 0.3
        form.setValue('valorComissao', applyMaskBrlCurrency(valorComissao.toFixed(2)))
        return
      }

      if (valorDesconto > valor * 0.3) {
        valorDesconto = valor * 0.3
        form.setValue('valorDesconto', applyMaskBrlCurrency(valorDesconto.toFixed(2)))
        return
      }

      let valorCusto = valorDesconto + valorComissao

      if (valorCusto > valor) {
        console.log('Ajustando valores para não ultrapassar o total...')
        valorCusto = valor
        valorDesconto = 0
        valorComissao = 0
        form.setValue('valorDesconto', applyMaskBrlCurrency(valorDesconto.toFixed(2)))
        form.setValue('valorComissao', applyMaskBrlCurrency(valorComissao.toFixed(2)))
        return
      }

      form.setValue('valorCusto', applyMaskBrlCurrency(valorCusto.toFixed(2)))
    }

    const subscription = form.watch((_value, { name }) => {
      if (['valorDesconto', 'valorComissao', 'valor'].includes(name ?? '')) {
        calcularValores()
      }
    })
    
    if (formValues.comissaoPercentual === false) {
      form.setValue('valorComissao', '0,00')
    }

    return () => subscription.unsubscribe()
  }, [form])

  const formValues = form.watch()

  // useEffect(() => {
  //   if (formValues.comissaoPercentual === false) {
  //     form.setValue('valorComissao', '0,00')
  //   }
  // }, [formValues.comissaoPercentual])

  useEffect(() => {
    console.log(formValues)
  }, [formValues])

  const {
    formState: { errors }
  } = form

  useEffect(() => {
    console.log('Erros do formulário:', errors)
  }, [errors])

  const onSubmit = async (formData: PlanFormData) => {
    if (selectedUnits?.length === 0) {
      showToast('Selecione ao menos uma unidade', 'error')
      return
    }

    const formattedData = {
      ...formData,
      unidade: {
        id: formData.unidade.id,
        nome: formData.unidade.nomeFantasia
      },
      valor: formatCurrencyToNumber(formData.valor),
      valorComissao: formatCurrencyToNumber(formData.valorComissao),
      valorCusto: formatCurrencyToNumber(formData.valorCusto),
      valorDesconto: formatCurrencyToNumber(formData.valorDesconto),
      dataInicio: formatDateString(formData.dataInicio),
      dataVencimento: formatDateString(formData.dataVencimento),
      unidades: selectedUnits.map((unit: any) => {
        return { id: unit.value }
      }),
      quantidade: !isEditMode && !quantityLimit ? 99999 : formData.quantidade
    }

    try {
      if (!isEditMode) {
        delete formattedData.id
        await httpClientPost(Endpoints.plans, { ...formattedData })
      } else {
        await httpClientPut(Endpoints.plans, { ...formattedData })
      }
      closeDialog?.()
      updateList?.()
      showToast('Plano salvo com sucesso', 'success')
      form.reset()
      setSelectedUnits([])
    } catch (error: any) {
      console.error('Erro no envio:', error?.response?.data?.errors)
      const errors = error?.response?.data?.errors
      if (errors) {
        for (const key in errors) {
          showToast(errors[key], 'error')
        }
      }
      showToast('Erro ao enviar requisição', 'error')
      //form.reset()
      //setSelectedUnits([])
    }
  }

  const registerWithMask = useHookFormMask(form.register)

  useEffect(() => {
    reloadContracts()
  }, [params])

  return (
    <Form {...form}>
      <form id="formPlan" onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col gap-3 pl-1 pr-4">
        <FormField
          control={form.control}
          name="nome"
          render={({ field }) => (
            <FormItem>
              <FormLabel>
                <span className="text-gray-400">*</span>
                Nome
              </FormLabel>
              <FormControl>
                <Input {...field} placeholder="nome" />
              </FormControl>
              <FormMessage className="text-xs">{form.formState.errors.nome?.message}</FormMessage>
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="descricao"
          render={({ field }) => (
            <FormItem>
              <FormLabel>
                <span className="text-gray-400"></span>
                Descrição
              </FormLabel>
              <FormControl>
                <Input {...field} placeholder="Descrição" />
              </FormControl>
              <FormMessage className="text-xs">{form.formState.errors.descricao?.message}</FormMessage>
            </FormItem>
          )}
        />

        <div className="grid grid-cols-6 gap-1 w-full">
          <FormField
            control={form.control}
            name="vendaOnline"
            render={({ field }) => (
              <FormItem className="w-full">
                <FormLabel>
                  {' '}
                  <span className="text-gray-400">*</span> Venda Online
                </FormLabel>
                <FormControl>
                  <SwitchDemo label="" value={field.value} onChange={field.onChange} />
                </FormControl>
                <FormMessage className="text-xs">{form.formState.errors?.vendaOnline?.message}</FormMessage>
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="comissaoPercentual"
            render={({ field }) => (
              <FormItem className="w-full">
                <FormLabel>
                  {' '}
                  <span className="text-gray-400">*</span> Comissão{' '}
                </FormLabel>
                <FormControl>
                  <SwitchDemo label="" value={field.value} onChange={field.onChange} />
                </FormControl>
                <FormMessage className="text-xs">{form.formState.errors?.vendaOnline?.message}</FormMessage>
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="valor"
            render={({ field: { ref, ...field } }) => (
              <FormItem>
                <FormLabel>
                  {' '}
                  <span className="text-gray-400">*</span>Valor
                </FormLabel>
                <FormControl>
                  <NumericFormat
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                    thousandSeparator="."
                    decimalSeparator=","
                    prefix="R$ "
                    decimalScale={2}
                    getInputRef={ref}
                    {...field}
                  />
                </FormControl>
                <FormMessage className="text-xs">{form.formState.errors.valor?.message}</FormMessage>
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="valorDesconto"
            render={({ field: { ref, ...field } }) => (
              <FormItem>
                <FormLabel>
                  {' '}
                  <span className="text-gray-400">*</span>Valor Desconto
                </FormLabel>
                <FormControl>
                  <NumericFormat
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                    thousandSeparator="."
                    decimalSeparator=","
                    prefix="R$ "
                    decimalScale={2}
                    getInputRef={ref}
                    {...field}
                  />
                </FormControl>
                <FormMessage className="text-xs">{form.formState.errors.valorDesconto?.message}</FormMessage>
              </FormItem>
            )}
          />

          {form.getValues('comissaoPercentual') === true ? (
            <FormField
              control={form.control}
              name="valorComissao"
              render={({ field: { ref, ...field } }) => (
                <FormItem>
                  <FormLabel>
                    {' '}
                    <span className="text-gray-400">*</span>Valor Comissão
                  </FormLabel>
                  <FormControl>
                    <NumericFormat
                      className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                      thousandSeparator="."
                      decimalSeparator=","
                      prefix="R$ "
                      decimalScale={2}
                      getInputRef={ref}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage className="text-xs">{form.formState.errors.valorComissao?.message}</FormMessage>
                </FormItem>
              )}
            />
          ) : (
            <></>
          )}

          <FormField
            control={form.control}
            name="valorCusto"
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  {' '}
                  <span className="text-gray-400">*</span>Valor de Custo
                </FormLabel>
                <FormControl>
                  <Input {...field} placeholder="Valor de Custo" readOnly={true} />
                </FormControl>
                <FormMessage className="text-xs">{form.formState.errors.valorCusto?.message}</FormMessage>
              </FormItem>
            )}
          />
        </div>
        <Select
          isMulti
          options={dataUnits}
          className="basic-multi-select w-full h-fit border-black"
          styles={{
            valueContainer(base: any) {
              return {
                ...base,
                overflowY: 'hidden',
                height: 36
              }
            }
          }}
          defaultValue={selectedUnits || []}
          classNamePrefix="select"
          value={selectedUnits}
          onChange={(e: any) => {
            setSelectedUnits(e as any)
          }}
          placeholder="Selecione as unidades"
        />
        <div className="grid grid-cols-2 gap-2 w-full">
          <FormField
            control={form.control}
            name="centroCusto.id"
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  {' '}
                  <span className="text-gray-400">*</span>Centro de Custo
                </FormLabel>
                <FormControl>
                  <ComboSelectWithLabel
                    defaultValue={field.value}
                    data={dataCostCenter}
                    onChange={(e) => {
                      field.onChange(e.value)
                    }}
                  />
                </FormControl>
                <FormMessage className="text-xs">{form.formState.errors.centroCusto?.id?.message}</FormMessage>
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="contrato.id"
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  {' '}
                  <span className="text-gray-400">*</span>Contrato
                </FormLabel>
                <FormControl>
                  <ComboSelectWithLabel
                    defaultValue={field.value}
                    data={contracts || []}
                    onChange={(e) => {
                      field.onChange(e.value)
                    }}
                  />
                </FormControl>
                <FormMessage className="text-xs">{form.formState.errors.maximoParcelas?.message}</FormMessage>
              </FormItem>
            )}
          />
        </div>

        <div className="grid grid-cols-2 gap-2 w-full">
          <FormField
            control={form.control}
            name="dataInicio"
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  {' '}
                  <span className="text-gray-400">*</span> Data de Início
                </FormLabel>
                <FormControl>
                  <Input {...field} {...registerWithMask('dataInicio', '99/99/9999')} placeholder="dd/mm/yyyy" />
                </FormControl>
                <FormMessage className="text-xs">{form.formState.errors.dataInicio?.message}</FormMessage>
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="dataVencimento"
            render={({ field }) => (
              <FormItem>
                <FormLabel> Data de Vencimento</FormLabel>
                <FormControl>
                  <Input {...field} {...registerWithMask('dataVencimento', '99/99/9999')} placeholder="dd/mm/yyyy" />
                </FormControl>
                <FormMessage className="text-xs">{form.formState.errors.dataVencimento?.message}</FormMessage>
              </FormItem>
            )}
          />
        </div>
        <div className="grid grid-cols-2 gap-2 w-full">
          {!isEditMode ? (
            <div className="grid grid-cols-2 gap-2 w-full">
              <div className="pt-8">
                <SwitchDemo label="Quantidade Limitada" value={quantityLimit} onChange={(value) => setQuantityLimit(value)} />
              </div>
              <FormField
                control={form.control}
                name="quantidade"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      <span className="text-gray-400"></span>Quantidade
                    </FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        type="number"
                        min={0}
                        onChange={(e) => {
                          const value = e.target.value
                          field.onChange(value ? parseInt(value, 10) : 0)
                        }}
                        disabled={isEditMode || !quantityLimit}
                      />
                    </FormControl>
                    <FormMessage className="text-xs">{form.formState.errors.quantidade?.message}</FormMessage>
                  </FormItem>
                )}
              />
            </div>
          ) : (
            <></>
          )}
          <FormField
            control={form.control}
            name="maximoParcelas"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Parcelamento Máximo:</FormLabel>
                <CustomSelect onValueChange={field.onChange} defaultValue={field.value}>
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Selecione..." />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    <SelectItem value="1">1x</SelectItem>
                    <SelectItem value="2">2x</SelectItem>
                    <SelectItem value="3">3x</SelectItem>
                    <SelectItem value="4">4x</SelectItem>
                    <SelectItem value="5">5x</SelectItem>
                    <SelectItem value="6">6x</SelectItem>
                    <SelectItem value="7">7x</SelectItem>
                    <SelectItem value="8">8x</SelectItem>
                    <SelectItem value="9">9x</SelectItem>
                    <SelectItem value="10">10x</SelectItem>
                    <SelectItem value="11">11x</SelectItem>
                    <SelectItem value="12">12x</SelectItem>
                  </SelectContent>
                </CustomSelect>
                <FormMessage className="text-xs">{form.formState.errors.maximoParcelas?.message}</FormMessage>
              </FormItem>
            )}
          />
        </div>
        <FormField
          control={form.control}
          name="observacao"
          render={({ field }) => (
            <FormItem>
              <FormControl>
                <Textarea className="" {...field} placeholder="Observações..." />
              </FormControl>
              <FormMessage className="text-xs">{form.formState.errors?.observacao?.message}</FormMessage>
            </FormItem>
          )}
        />

        {!isEditMode && (
          <Button type="submit" className="bg-acelerafit-green-400 text-muted  font-bold hover:bg-acelerafit-green-300 transition-all mt-3 ">
            Enviar
          </Button>
        )}
      </form>
    </Form>
  )
}
